import type { LiveGameEvent } from '../liveMatchTypes.ts';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ScorePoint {
  rally: number;
  home: number;
  away: number;
}

export interface SetProgression {
  setNumber: number;
  points: ScorePoint[];
}

export interface Run {
  team: 'HOME' | 'AWAY';
  length: number;
  startScore: { home: number; away: number };
  setNumber: number;
}

export interface ServeStats {
  setNumber: number;
  homeServePoints: number;
  homeBreakPoints: number;
  awayServePoints: number;
  awayBreakPoints: number;
}

export interface TimeoutEffect {
  setNumber: number;
  callingTeam: 'HOME' | 'AWAY';
  scoreBefore: { home: number; away: number };
  pointsAfter: { home: number; away: number };
  wasEffective: boolean;
}

export interface RotationStats {
  setterPosition: number;
  homePoints: number;
  awayPoints: number;
}

export interface KeyMoment {
  type: 'MAX_LEAD' | 'COMEBACK' | 'TIGHT_SET';
  setNumber: number;
  team: 'HOME' | 'AWAY';
  score: { home: number; away: number };
  description: string;
}

// ─── computeSetProgression ───────────────────────────────────────────────────

export function computeSetProgression(events: LiveGameEvent[]): SetProgression[] {
  const bySet = new Map<number, ScorePoint[]>();

  for (const evt of events) {
    if (evt.type !== 'POINT_HOME' && evt.type !== 'POINT_AWAY') continue;
    const s = evt.setNumber;
    if (!bySet.has(s)) bySet.set(s, [{ rally: 0, home: 0, away: 0 }]);
    const pts = bySet.get(s)!;
    pts.push({ rally: pts.length, home: evt.homePoints, away: evt.awayPoints });
  }

  return [...bySet.entries()]
    .sort(([a], [b]) => a - b)
    .map(([setNumber, points]) => ({ setNumber, points }));
}

// ─── computeRuns ─────────────────────────────────────────────────────────────

export function computeRuns(events: LiveGameEvent[], minLength = 3): Run[] {
  const runs: Run[] = [];
  const pts = events.filter((e) => e.type === 'POINT_HOME' || e.type === 'POINT_AWAY');
  if (pts.length === 0) return runs;

  let team: 'HOME' | 'AWAY' = pts[0].type === 'POINT_HOME' ? 'HOME' : 'AWAY';
  let len = 1;
  let startScore = { home: pts[0].snapshot.homePoints, away: pts[0].snapshot.awayPoints };
  let setNum = pts[0].setNumber;

  const flush = () => {
    if (len >= minLength) runs.push({ team, length: len, startScore, setNumber: setNum });
  };

  for (let i = 1; i < pts.length; i++) {
    const evt = pts[i];
    const t: 'HOME' | 'AWAY' = evt.type === 'POINT_HOME' ? 'HOME' : 'AWAY';
    if (t === team && evt.setNumber === setNum) {
      len++;
    } else {
      flush();
      team = t;
      len = 1;
      startScore = { home: evt.snapshot.homePoints, away: evt.snapshot.awayPoints };
      setNum = evt.setNumber;
    }
  }
  flush();

  return runs;
}

// ─── computeServeStats ───────────────────────────────────────────────────────

export function computeServeStats(events: LiveGameEvent[]): ServeStats[] {
  const bySet = new Map<number, ServeStats>();

  for (const evt of events) {
    if (evt.type !== 'POINT_HOME' && evt.type !== 'POINT_AWAY') continue;
    const s = evt.setNumber;
    if (!bySet.has(s)) {
      bySet.set(s, { setNumber: s, homeServePoints: 0, homeBreakPoints: 0, awayServePoints: 0, awayBreakPoints: 0 });
    }
    const stat = bySet.get(s)!;
    const serving = evt.snapshot.servingTeamNow;
    const scored = evt.type === 'POINT_HOME' ? 'HOME' : 'AWAY';

    if (serving === 'HOME') {
      if (scored === 'HOME') stat.homeServePoints++;
      else stat.awayBreakPoints++;
    } else if (serving === 'AWAY') {
      if (scored === 'AWAY') stat.awayServePoints++;
      else stat.homeBreakPoints++;
    }
  }

  return [...bySet.entries()].sort(([a], [b]) => a - b).map(([, v]) => v);
}

// ─── computeTimeoutEffects ───────────────────────────────────────────────────

export function computeTimeoutEffects(events: LiveGameEvent[]): TimeoutEffect[] {
  const effects: TimeoutEffect[] = [];

  for (let i = 0; i < events.length; i++) {
    const evt = events[i];
    if (evt.type !== 'TIMEOUT_HOME' && evt.type !== 'TIMEOUT_AWAY') continue;

    const callingTeam: 'HOME' | 'AWAY' = evt.type === 'TIMEOUT_HOME' ? 'HOME' : 'AWAY';
    const scoreBefore = { home: evt.homePoints, away: evt.awayPoints };

    const pointsAfter = { home: 0, away: 0 };
    let count = 0;
    for (let j = i + 1; j < events.length && count < 3; j++) {
      const next = events[j];
      if (next.setNumber !== evt.setNumber) break;
      if (next.type === 'POINT_HOME') { pointsAfter.home++; count++; }
      else if (next.type === 'POINT_AWAY') { pointsAfter.away++; count++; }
    }

    if (count === 0) continue;

    const callerPoints = callingTeam === 'HOME' ? pointsAfter.home : pointsAfter.away;
    const wasEffective = callerPoints >= Math.ceil(count / 2);

    effects.push({ setNumber: evt.setNumber, callingTeam, scoreBefore, pointsAfter, wasEffective });
  }

  return effects;
}

// ─── computeRotationStats ────────────────────────────────────────────────────

export function computeRotationStats(events: LiveGameEvent[]): RotationStats[] {
  const stats: Record<number, RotationStats> = {};
  for (let i = 1; i <= 6; i++) stats[i] = { setterPosition: i, homePoints: 0, awayPoints: 0 };

  for (const evt of events) {
    if (evt.type !== 'POINT_HOME' && evt.type !== 'POINT_AWAY') continue;
    const setter = evt.snapshot.homeLineup.find((fp) => fp.role === 'PALLEGGIO');
    if (!setter) continue;
    const pos = setter.position;
    if (pos < 1 || pos > 6) continue;
    if (evt.type === 'POINT_HOME') stats[pos].homePoints++;
    else stats[pos].awayPoints++;
  }

  return Object.values(stats).sort((a, b) => a.setterPosition - b.setterPosition);
}

// ─── computeKeyMoments ───────────────────────────────────────────────────────

export function computeKeyMoments(events: LiveGameEvent[]): KeyMoment[] {
  const moments: KeyMoment[] = [];
  const pts = events.filter((e) => e.type === 'POINT_HOME' || e.type === 'POINT_AWAY');
  const sets = [...new Set(pts.map((e) => e.setNumber))].sort((a, b) => a - b);

  for (const setNum of sets) {
    const sp = pts.filter((e) => e.setNumber === setNum);
    if (sp.length === 0) continue;

    let maxHomeLead = 0; let maxHomeLeadScore = { home: 0, away: 0 };
    let maxAwayLead = 0; let maxAwayLeadScore = { home: 0, away: 0 };
    let maxHomeDeficit = 0; let homeComeback = false;
    let maxAwayDeficit = 0; let awayComeback = false;

    for (const evt of sp) {
      const diff = evt.homePoints - evt.awayPoints;

      if (diff > maxHomeLead) { maxHomeLead = diff; maxHomeLeadScore = { home: evt.homePoints, away: evt.awayPoints }; }
      if (-diff > maxAwayLead) { maxAwayLead = -diff; maxAwayLeadScore = { home: evt.homePoints, away: evt.awayPoints }; }

      maxHomeDeficit = Math.max(maxHomeDeficit, -diff);
      maxAwayDeficit = Math.max(maxAwayDeficit, diff);

      if (!homeComeback && maxHomeDeficit >= 3 && diff >= 0) {
        homeComeback = true;
        moments.push({ type: 'COMEBACK', setNumber: setNum, team: 'HOME', score: { home: evt.homePoints, away: evt.awayPoints }, description: `Rimonta sul ${evt.homePoints}-${evt.awayPoints}` });
      }
      if (!awayComeback && maxAwayDeficit >= 3 && diff <= 0) {
        awayComeback = true;
        moments.push({ type: 'COMEBACK', setNumber: setNum, team: 'AWAY', score: { home: evt.homePoints, away: evt.awayPoints }, description: `Rimonta sul ${evt.homePoints}-${evt.awayPoints}` });
      }
    }

    if (maxHomeLead >= 3) {
      moments.push({ type: 'MAX_LEAD', setNumber: setNum, team: 'HOME', score: maxHomeLeadScore, description: `Vantaggio massimo +${maxHomeLead} (${maxHomeLeadScore.home}-${maxHomeLeadScore.away})` });
    }
    if (maxAwayLead >= 3) {
      moments.push({ type: 'MAX_LEAD', setNumber: setNum, team: 'AWAY', score: maxAwayLeadScore, description: `Vantaggio massimo +${maxAwayLead} (${maxAwayLeadScore.home}-${maxAwayLeadScore.away})` });
    }

    const last = sp[sp.length - 1];
    const finalDiff = Math.abs(last.homePoints - last.awayPoints);
    if (finalDiff <= 2 && (last.homePoints >= 23 || last.awayPoints >= 23)) {
      moments.push({ type: 'TIGHT_SET', setNumber: setNum, team: last.homePoints > last.awayPoints ? 'HOME' : 'AWAY', score: { home: last.homePoints, away: last.awayPoints }, description: `Set combattuto: ${last.homePoints}-${last.awayPoints}` });
    }
  }

  return moments;
}
